import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { SUPPORTED_LANG_CODES, DEFAULT_LANG } from "@/i18n";

const SITE_ORIGIN = "https://atlasforgefinancial.com";

// Locale codes → SEO-friendly `hreflang` values. Some search engines expect
// region-flavoured tags (pt-BR, es-419) so we map explicitly here rather
// than shipping the bare two-letter code.
const HREFLANG_MAP = {
  en: "en",
  es: "es",
  fr: "fr",
  de: "de",
  pt: "pt-BR",
  ja: "ja",
};

/**
 * Build a locale-prefixed absolute URL. English stays at the canonical
 * root URL (no `/en` prefix), which matches Stripe / Linear / Vercel.
 */
function absoluteUrl(pathWithoutLang, lang) {
  const clean = pathWithoutLang.startsWith("/") ? pathWithoutLang : `/${pathWithoutLang}`;
  const localized = lang === DEFAULT_LANG ? clean : `/${lang}${clean === "/" ? "" : clean}`;
  return `${SITE_ORIGIN}${localized}`;
}

/**
 * Strip any `/<supported-lang>` prefix from a pathname so we can rebuild
 * all hreflang variants off the same underlying route.
 */
function stripLangPrefix(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length && SUPPORTED_LANG_CODES.includes(segs[0])) {
    segs.shift();
  }
  return "/" + segs.join("/");
}

/**
 * <Seo /> emits localized <title>, <meta description>, canonical, hreflang
 * alternates, Open Graph tags, and any JSON-LD blocks passed in. Also
 * updates document.title dynamically for SPA nav.
 *
 * Props:
 *   title         — page title (already localized by caller when relevant)
 *   description   — meta description
 *   path          — absolute path without lang prefix (e.g. "/about"). If
 *                   omitted, the current URL is used.
 *   type          — Open Graph type, defaults to "website"
 *   image         — absolute URL for og:image
 *   jsonLd        — array of structured-data objects
 *   noindex       — set true for admin / debug pages
 */
export default function Seo({
  title,
  description,
  path,
  type = "website",
  image,
  jsonLd,
  noindex = false,
  preloadImages = [],
}) {
  const { i18n, t } = useTranslation();
  const { lang: langParam } = useParams();
  const location = useLocation();
  const activeLang = SUPPORTED_LANG_CODES.includes(langParam) ? langParam : DEFAULT_LANG;

  const siteName = t("meta.siteName", { defaultValue: "AtlasForge Financial" });
  const fullTitle = title ? `${title} · ${siteName}` : siteName;
  const desc = description || t("meta.defaultDescription");

  // Derive the underlying path (no lang prefix) for alt tag generation.
  const langlessPath = path || stripLangPrefix(location.pathname) || "/";
  const canonical = absoluteUrl(langlessPath, activeLang);
  const ogImage = image || `${SITE_ORIGIN}/logo512.png`;

  return (
    <Helmet>
      <html lang={HREFLANG_MAP[activeLang] || activeLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* hreflang alternates so Google shows the right locale in SERPs. */}
      {SUPPORTED_LANG_CODES.map((code) => (
        <link
          key={code}
          rel="alternate"
          hrefLang={HREFLANG_MAP[code] || code}
          href={absoluteUrl(langlessPath, code)}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={absoluteUrl(langlessPath, DEFAULT_LANG)} />

      {/* Open Graph / Twitter */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={(HREFLANG_MAP[activeLang] || activeLang).replace("-", "_")} />
      {SUPPORTED_LANG_CODES.filter((c) => c !== activeLang).map((c) => (
        <meta key={c} property="og:locale:alternate" content={(HREFLANG_MAP[c] || c).replace("-", "_")} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {(jsonLd || []).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {/* Preload above-the-fold hero images so LCP paints instantly on cold loads. */}
      {(preloadImages || []).map((src, i) => (
        <link key={`preload-${i}`} rel="preload" as="image" href={src} fetchpriority="high" />
      ))}
    </Helmet>
  );
}
